import { FC, useCallback, useMemo, useState } from "react";
import { format, differenceInSeconds } from "date-fns";
import cn from "classnames";
import { formatBytes } from "@epcnetwork/core-ui-kit";
import {
  Button,
  notification,
  Menu,
  Chip,
  Tooltip,
  TableCell,
  TableRow,
  useWindowSize,
  useCall,
  MenuButton,
  TextEllipsis,
  ExtractComponent,
} from "@epcnetwork/core-ui-kit";

import { getPipelineNameFromResponse } from "utils/pipelines.utils";
import { JobModel } from "models";
import { useUser } from "hooks";
import { CircularProgress } from "components";
import { deleteJob, getJobFile } from "api";
import { StartedAtCell } from "./table-cell-started-at/table-cell-started-at";
import { highlightTime } from "./job-row.constants";

import clockImg from "assets/images/clock.svg";
import errorImg from "assets/images/circle-question.svg";
import downloadImg from "assets/images/circle-download.svg";
import calendarImg from "assets/images/calendar.svg";
import styles from "./job-row.module.css";

type RowProps = {
  item: JobModel;
  refresh: VoidFunction;
};

type AppearanceMap = {
  [key: string]: ExtractComponent<typeof Chip>["appearance"];
};

const chipAppearance: AppearanceMap = {
  done: "success",
  "in-progress": "primary",
  queued: "warning",
  failed: "error",
  preprocessing: "primary",
};

export const JobRow: FC<RowProps> = ({ item, refresh }) => {
  const { isAdmin, user } = useUser();
  const { width } = useWindowSize();

  const [isDeleted, setDeleted] = useState(false);

  const {
    submit: submitDeleteJob,
    onCallSuccess: onDeleteSuccess,
    onCallError: onDeleteError,
  } = useCall(deleteJob);
  onDeleteSuccess(() => {
    setDeleted(true);
    refresh();
    notification.success("Job deleted", "Successfully deleted job");
  });
  onDeleteError((error) => {
    notification.error("Deleting error", error.message);
  });

  const { submit, submitting, onCallSuccess, onCallError } = useCall(getJobFile);
  onCallSuccess((payload) => {
    handleDownloadFile(payload.downloadUrl);
  });
  onCallError((error) => {
    notification.error("Cannot download file", error.message);
  });

  const createdAtDate = new Date(item.createdAt);
  const startAtDate = item.startedAt ? new Date(item.startedAt) : undefined;
  const finishedAtDate = new Date(item.finishedAt);

  const isPaused = item.status === "queued";
  const isDone = item.status === "done";
  const isFailed = item.status === "failed";
  const isPreprocessing = item.status === "preprocessing";
  const isProgress = !isDone && !isFailed && !isPreprocessing;
  const isMobile = width < 769;

  const progress = isDone ? 100 : item.progress || 0;
  const isHighlight = differenceInSeconds(new Date(), finishedAtDate) <= highlightTime;

  const handleDownloadFile = (downloadUrl: string) => {
    if (downloadUrl) {
      window.open(downloadUrl, "_blank");
    } else {
      notification.error("Downloading error", "Cannot download given file, no download url.");
    }
  };

  const handleGetFile = () => {
    if (item.outputFilename) {
      submit({ data: { fileName: item.outputFilename } });
    } else {
      notification.error("Downloading error", "Cannot download file, try to refresh page");
    }
  };

  const statusAppearance = useMemo(() => {
    return chipAppearance[item.status] || "primary";
  }, [item.status]);

  const onSubmitDelete = useCallback(() => {
    submitDeleteJob({ params: { jobId: item.id } });
  }, [item.id, submitDeleteJob]);

  const handleDelete = useCallback(() => {
    notification.confirm("Deleting job?", "Are you sure you want to delete this job?", {
      onOk: () => onSubmitDelete(),
      okText: "Delete",
      icon: "delete",
    });
  }, [onSubmitDelete]);

  const pipeline = getPipelineNameFromResponse(item.pipelineName);

  const status = (
    <div className={cn(styles.actions, { [styles.actionsMobile]: isMobile })}>
      {isProgress && (
        <CircularProgress
          value={progress}
          isPaused={isPaused}
          isFailed={isFailed}
          isDone={isDone}
        />
      )}

      {isDone && (
        <Button
          onClick={handleGetFile}
          appearance="none"
          disabled={submitting}
          className={styles.downloadBtn}
        >
          <img src={downloadImg} width={25} height={25} alt="Download" />
        </Button>
      )}
      {isFailed && (
        <Tooltip
          triggerElement={() => <img src={errorImg} width={25} height={25} alt="Error" />}
          position="bottom"
        >
          <div className={styles.errorTooltip}>
            <div className={styles.errorTitle}>Job failed</div>
            {item.failedReason}
          </div>
        </Tooltip>
      )}
    </div>
  );

  const canDelete = useMemo(() => {
    if (item.status === "in progress") return false;
    if (item.status === "preprocessing") return false;
    if (isAdmin) return true;
    if (item?.userId === user?.id) return true;
    if (item.groupId === user?.groupId) return true;

    return false;
  }, [item, user, isAdmin]);

  if (isDeleted) {
    return null;
  }

  return (
    <TableRow id={item.id} className={cn({ [styles.highlight]: isHighlight })}>
      <TableCell className={styles.long}>
        <div className={styles.column}>
          <div>
            <TextEllipsis
              tooltipTrigger="hover"
              tooltipPosition="bottom-left"
              className={styles.info}
            >
              {item.filename}
            </TextEllipsis>
          </div>
          {!!item.fileSize && <div className={styles.fileSize}>{formatBytes(item.fileSize)}</div>}
        </div>
      </TableCell>
      <TableCell className={styles.long}>
        <TextEllipsis tooltipTrigger="hover" tooltipPosition="bottom-left" className={styles.info}>
          {pipeline?.title}
        </TextEllipsis>
      </TableCell>
      <TableCell>
        <div className={styles.cell}>
          <div className={cn({ [styles.mobileWrapper]: isMobile })}>
            <Chip
              text={item.status}
              appearance={statusAppearance}
              className={cn(styles.chip, { [styles.mobileChip]: isMobile })}
            />
          </div>

          {isMobile && status}
        </div>
      </TableCell>
      <TableCell className={styles.startAtDate}>
        <StartedAtCell startedAt={startAtDate} onDeleteClick={onSubmitDelete} />
      </TableCell>
      <TableCell className={styles.long}>
        <TextEllipsis tooltipTrigger="hover" tooltipPosition="bottom-left" className={styles.info}>
          {item?.userEmail || "-"}
        </TextEllipsis>
      </TableCell>
      <TableCell className={styles.long}>
        <TextEllipsis tooltipTrigger="hover" tooltipPosition="bottom-right" className={styles.info}>
          {item?.groupName || "-"}
        </TextEllipsis>
      </TableCell>
      <TableCell>
        <TextEllipsis
          tooltipTrigger="hover"
          tooltipPosition="bottom-right"
          className={styles.databases}
        >
          {item.databases?.join(", ")}
        </TextEllipsis>
      </TableCell>
      <TableCell>
        <div className={styles.date}>
          <div className={styles.dateItem}>
            <img src={calendarImg} alt="" width={11} height={11} />
            {createdAtDate ? format(createdAtDate, "MMM dd, yyyy") : "-"}
          </div>
          <div className={styles.dateItem}>
            <img src={clockImg} alt="" width={11} height={11} />
            {createdAtDate ? format(createdAtDate, "hh:mm aaa") : "-"}
          </div>
        </div>
      </TableCell>
      <TableCell>{status}</TableCell>
      <TableCell>
        {canDelete && (
          <Menu>
            <MenuButton
              appearance="delete"
              onClick={handleDelete}
              icon="delete"
              disabled={!isDone && !isFailed}
            >
              Delete
            </MenuButton>
          </Menu>
        )}
        {!canDelete && <div style={{ height: "30px" }} />}
      </TableCell>
    </TableRow>
  );
};
